import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { STAT_ICONS } from "@/lib/statIcons";
import ImageField from "@/components/admin/ImageField";

const inputCls = "w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-sm outline-none focus:border-srs";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-mute";

export default function SettingsTab() {
  const qc = useQueryClient();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site").then(({ data }) => setS({
      hero_badge: "", hero_headline: "", hero_subhead: "", hero_image: "",
      phone: "", email: "", whatsapp: "", address: "", map_query: "",
      catalog_pdf: "", testimonials: [], socials: { facebook: "", instagram: "", youtube: "" },
      ...data.settings,
      socials: { facebook: "", instagram: "", youtube: "", ...(data.settings?.socials || {}) },
    }));
  }, []);

  if (!s) return <p className="font-mono text-xs uppercase tracking-[0.25em] text-subtle">Loading…</p>;

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setT = (i, k, v) => setS((p) => ({ ...p, testimonials: p.testimonials.map((t, j) => (j === i ? { ...t, [k]: v } : t)) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", s);
      qc.invalidateQueries({ queryKey: ["site"] });
      toast.success("Site content saved.");
    } catch (e) {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-10">
      <section className="rounded-3xl border border-line bg-white p-6 shadow-[0_14px_35px_-24px_rgba(13,13,17,0.18)]">
        <h2 className="mb-5 font-display text-lg font-bold tracking-tight">Hero</h2>
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Badge text</label>
            <input className={inputCls} value={s.hero_badge} onChange={(e) => set("hero_badge", e.target.value)} data-testid="admin-hero-badge-input" />
          </div>
          <div>
            <label className={labelCls}>Headline (new line = new masked line)</label>
            <textarea rows={2} className={inputCls} value={s.hero_headline} onChange={(e) => set("hero_headline", e.target.value)} data-testid="admin-hero-text-edit-input" />
          </div>
          <div>
            <label className={labelCls}>Subhead</label>
            <textarea rows={2} className={inputCls} value={s.hero_subhead} onChange={(e) => set("hero_subhead", e.target.value)} />
          </div>
          <ImageField label="Hero image" value={s.hero_image} onChange={(v) => set("hero_image", v)} testid="admin-hero-image" />
        </div>
      </section>

      <section className="rounded-3xl border border-line bg-white p-6 shadow-[0_14px_35px_-24px_rgba(13,13,17,0.18)]">
        <h2 className="mb-5 font-display text-lg font-bold tracking-tight">Contact & Catalog</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Phone</label>
            <input className={inputCls} value={s.phone} onChange={(e) => set("phone", e.target.value)} data-testid="admin-phone-input" />
          </div>
          <div>
            <label className={labelCls}>Email (optional)</label>
            <input className={inputCls} value={s.email} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>WhatsApp number (optional, plain wa.me link)</label>
            <input className={inputCls} value={s.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="916366634517" />
          </div>
          <div>
            <label className={labelCls}>Map search query</label>
            <input className={inputCls} value={s.map_query} onChange={(e) => set("map_query", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Address</label>
            <input className={inputCls} value={s.address} onChange={(e) => set("address", e.target.value)} data-testid="admin-address-input" />
          </div>
          <div className="sm:col-span-2">
            <ImageField label="Product catalog PDF" value={s.catalog_pdf} onChange={(v) => set("catalog_pdf", v)} accept="application/pdf" testid="admin-catalog-pdf" />
          </div>
          {["facebook", "instagram", "youtube"].map((k) => (
            <div key={k}>
              <label className={labelCls}>{k} URL</label>
              <input className={inputCls} value={s.socials[k]} onChange={(e) => set("socials", { ...s.socials, [k]: e.target.value })} placeholder="https://…" />
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-3xl border border-line bg-white p-6 shadow-[0_14px_35px_-24px_rgba(13,13,17,0.18)]">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold tracking-tight">Testimonials</h2>
          <button
            type="button"
            data-testid="admin-testimonial-add"
            onClick={() => set("testimonials", [...s.testimonials, { name: "", location: "", text: "" }])}
            className="flex items-center gap-2 border border-ink px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-ink hover:text-white"
          >
            <Plus size={13} /> Add
          </button>
        </div>
        <div className="space-y-4">
          {s.testimonials.map((t, i) => (
            <div key={i} className="space-y-3 border border-line p-4" data-testid={`admin-testimonial-${i}`}>
              <div className="grid gap-3 sm:grid-cols-2">
                <input className={inputCls} placeholder="Name" value={t.name} onChange={(e) => setT(i, "name", e.target.value)} />
                <input className={inputCls} placeholder="Location" value={t.location} onChange={(e) => setT(i, "location", e.target.value)} />
              </div>
              <textarea rows={2} className={inputCls} placeholder="What they said" value={t.text} onChange={(e) => setT(i, "text", e.target.value)} />
              <button type="button" onClick={() => set("testimonials", s.testimonials.filter((_, j) => j !== i))} className="flex items-center gap-1.5 text-xs text-srs hover:text-srs-dark">
                <Trash2 size={13} /> Remove
              </button>
            </div>
          ))}
          {!s.testimonials.length && <p className="text-sm text-subtle">No testimonials yet.</p>}
        </div>
      </section>

      <section className="rounded-3xl border border-line bg-white p-6 shadow-[0_14px_35px_-24px_rgba(13,13,17,0.18)]">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold tracking-tight">Company Stats</h2>
          <button
            type="button"
            data-testid="admin-stat-add"
            onClick={() => set("stats", [...(s.stats || []), { icon: "building", value: 100, suffix: "+", label: "New Stat" }])}
            className="flex items-center gap-2 rounded-full border border-ink px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-ink hover:text-white"
          >
            <Plus size={13} /> Add
          </button>
        </div>
        <p className="mb-5 text-xs text-subtle">Shown right below the hero with a counting animation. Value is the number it counts up to.</p>
        <div className="space-y-3">
          {(s.stats || []).map((st, i) => (
            <div key={i} className="grid grid-cols-2 items-center gap-3 rounded-2xl border border-line p-4 sm:grid-cols-[110px_1fr_110px_70px_auto]" data-testid={`admin-stat-${i}`}>
              <select
                value={st.icon || "building"}
                onChange={(e) => set("stats", s.stats.map((x, j) => (j === i ? { ...x, icon: e.target.value } : x)))}
                className={inputCls}
                aria-label="Icon"
              >
                {Object.keys(STAT_ICONS).map((k) => (
                  <option key={k} value={k}>{k}</option>
                ))}
              </select>
              <input
                className={inputCls}
                placeholder="Label"
                value={st.label || ""}
                onChange={(e) => set("stats", s.stats.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
              />
              <input
                className={inputCls}
                type="number"
                placeholder="Value"
                value={st.value ?? 0}
                onChange={(e) => set("stats", s.stats.map((x, j) => (j === i ? { ...x, value: Number(e.target.value) } : x)))}
              />
              <input
                className={inputCls}
                placeholder="+"
                value={st.suffix || ""}
                onChange={(e) => set("stats", s.stats.map((x, j) => (j === i ? { ...x, suffix: e.target.value } : x)))}
              />
              <button
                type="button"
                onClick={() => set("stats", s.stats.filter((_, j) => j !== i))}
                className="flex items-center gap-1.5 text-xs text-srs hover:text-srs-dark"
                aria-label="Remove stat"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          {!(s.stats || []).length && <p className="text-sm text-subtle">No stats — defaults show on the site until you add your own.</p>}
        </div>
      </section>

      <button
        data-testid="admin-hero-text-save-button"
        onClick={save}
        disabled={saving}
        className="bg-srs px-8 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors hover:bg-srs-dark disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save all changes"}
      </button>
    </div>
  );
}
